import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, User, Eye } from "lucide-react";
import { PageHeader } from "@/components/layout/Navigation";
import api from "@/lib/api";

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const categories = ["Preventive Care", "Chronic Care", "Weight Management", "Men's Health", "Women's Health", "Wellness"];

  useEffect(() => {
    api.getBlogPosts(selectedCategory)
      .then(res => {
        setPosts(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, [selectedCategory]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#1B4D3E] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="blog-page">
      <PageHeader 
        title="Health & Wellness Blog"
        subtitle="Expert insights on health, wellness, and medical care."
        breadcrumbs={[{ name: "Blog" }]}
      />

      <section className="py-20 md:py-32 bg-[#F9F9F7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {/* Categories */}
          <div className="flex flex-wrap gap-3 mb-12">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                !selectedCategory 
                  ? "bg-[#1B4D3E] text-white" 
                  : "bg-white text-[#4A4A4A] border border-[#1B4D3E]/20 hover:border-[#1B4D3E]"
              }`}
              data-testid="category-all"
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === cat 
                    ? "bg-[#1B4D3E] text-white" 
                    : "bg-white text-[#4A4A4A] border border-[#1B4D3E]/20 hover:border-[#1B4D3E]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Posts Grid */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {posts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-3xl overflow-hidden border border-[#1B4D3E]/10 hover:border-[#D4AF37]/50 hover:shadow-xl transition-all group"
                data-testid={`blog-post-${index}`}
              >
                <Link to={`/blog/${post.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img 
                      src={post.image_url}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="text-xs tracking-[0.1em] uppercase text-[#D4AF37] font-medium">
                        {post.category}
                      </span>
                      <span className="text-xs text-[#888]">
                        {new Date(post.created_at).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </span>
                    </div>
                    <h3 className="font-display text-xl text-[#1A1A1A] mb-3 group-hover:text-[#1B4D3E] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-[#4A4A4A] font-light text-sm leading-relaxed mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-[#888]">
                        <User className="w-4 h-4" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#1B4D3E] font-medium text-sm group-hover:gap-3 transition-all">
                        Read <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </motion.div>

          {posts.length === 0 && (
            <div className="text-center py-20">
              <h3 className="font-display text-2xl text-[#1A1A1A] mb-4">No posts found</h3>
              <p className="text-[#4A4A4A] mb-6">Check back soon for new content.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
